import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, IconButton, List, Stack, styled, Typography } from '@mui/material'
import type { FC, ReactNode } from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { COLORS, ICONS, unit } from '@hcr/ui'

import { useLastPath } from '../../../../../contexts'
import { Path } from '../../../../../models'
import { to } from '../../../../../utils'
import { LayoutBase, Metadata } from '../../../../common'
import { OwnerBuyAccommodationDrawer } from '../../../../common/owner-buy-accommodation-drawer'
import { Link } from '../link'

interface LayoutContextMenuProps {
  cta: ReactNode
  list: ReactNode
}

export const LayoutContextMenu: FC<LayoutContextMenuProps> = ({ cta, list }) => {
  const { t } = useTranslation()
  const lastPath = useLastPath()
  const navigate = useNavigate()
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const openDrawer = () => setIsDrawerOpen(true)
  const closeDrawer = () => setIsDrawerOpen(false)

  return (
    <LayoutBase>
      <Metadata title={t('page-title.context-menu')} />
      <Container>
        <Header as='header'>
          <Stack>
            <IconButton
              onClick={() => navigate(lastPath.root)}
              aria-label={t('context-menu.close')}
              sx={{
                alignSelf: 'end',
                margin: `${unit(4.5)} ${unit(8)} 0 0`,
                padding: 0,
                fontSize: unit(6),
              }}
            >
              <FontAwesomeIcon icon={ICONS.farXmark} />
            </IconButton>
          </Stack>
          <Typography component='h1' variant='uppercaseS'>
            {t('context-menu.your-bookings')}
          </Typography>
        </Header>
        <Content as='main'>
          <List>{list}</List>
        </Content>
        <Footer as='footer'>
          <Link
            description={t('context-menu.explore-and-book-services')}
            icon={<FontAwesomeIcon icon={ICONS.farArrowRight} />}
            title={t('context-menu.browse-destinations')}
            to={to(Path.ContextMenuDestinations)}
          />
          <Link
            description={t('context-menu.find-your-next-holiday')}
            icon={<FontAwesomeIcon icon={ICONS.farArrowUpRightFromSquare} />}
            title={t('context-menu.book-a-vacation')}
            onClick={openDrawer}
            to={''}
          />
          <Cta>{cta}</Cta>
        </Footer>
      </Container>
      <OwnerBuyAccommodationDrawer isOpen={isDrawerOpen} onClose={closeDrawer} />
    </LayoutBase>
  )
}

const Container = styled(Box)`
  min-height: 100vh;
  background-color: ${COLORS.hiekka[500]};
  margin-bottom: ${unit(4)};
`

const Header = styled(Box)`
  background-color: ${COLORS.vaalea['60%']};
  padding: 0 0 ${unit(3.5)} ${unit(7)};
`

const Content = styled(Box)`
  background-color: ${COLORS.vaalea['60%']};
`

const Footer = styled(Box)`
  margin-top: ${unit(9)};
`

const Cta = styled(Box)`
  padding: ${unit(9)} ${unit(7)};
`
